import * as React from "react";
import { apiFetch } from "@/lib/api.js";
import type { PlanUsage } from "./PlanContext.js";

interface OnboardingStatus { hasSpaces: boolean; hasItems: boolean; hasIntent: boolean; hasReachedMilestone: boolean; spaceCount: number; itemCount: number; }
interface Invitation { id: string; email: string; status: string; householdId: string; invitedBy: string; invitedByName: string; createdAt: string; }
interface GuidedFlow { id: string; title: string; description: string | null; frequency: string; nextDueDate: string; tasks: unknown[]; userId: string; householdId: string; itemId: string | null; createdAt: string; }
interface Settings { aiEnabled: boolean; quickAddFields: string[]; declutterMonths: number; declutterSeasonalMonths: number; }

interface RecentItem { id: string; name: string; spaceName: string | null; createdAt: string; }

interface SpaceInfo { id: string; name: string; type: string; roomType: string | null; }

interface DashboardData {
  onboarding: OnboardingStatus;
  invitations: Invitation[];
  flows: GuidedFlow[];
  settings: Settings;
  plan: PlanUsage | null;
  lowStockCount?: number;
  unlocatedCount?: number;
  itemsWithPhotoCount?: number;
  recentItems?: RecentItem[];
  roomSpaces?: SpaceInfo[];
}

interface DashboardContextValue {
  data: DashboardData | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const DashboardContext = React.createContext<DashboardContextValue>({ data: null, loading: true, refresh: async () => {} });

export const useDashboard = () => React.useContext(DashboardContext);

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = React.useState<DashboardData | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    try {
      setData(await apiFetch<DashboardData>("/api/dashboard"));
    } catch {}
  }, []);

  React.useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  return <DashboardContext.Provider value={{ data, loading, refresh }}>{children}</DashboardContext.Provider>;
}
